import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import Badge from '../core/Badge/Badge';
import { LoadingSpinner } from '../common/LoadingSpinner';
import {
  Calendar, Clock, Video, MapPin, FileText, Users, ArrowLeft,
  Edit2, CheckCircle, XCircle, MessageSquare, User
} from 'lucide-react';
import { formatDate, formatTime } from '../../utils/formatters';
import api from '../../services/api';
import '../../styles/mentor-session-detail.scss';

interface SessionDetail {
  id: string;
  relationship_id: string;
  mentee_first_name: string;
  mentee_last_name: string;
  mentee_email?: string;
  scheduled_date: string;
  scheduled_time?: string;
  duration_minutes: number | null;
  title: string;
  description?: string;
  location?: string;
  meeting_link?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  meeting_notes?: string;
  mentee_feedback?: string;
  mentor_feedback?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
}

const SessionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    if (id) {
      loadSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/mentor/sessions/${id}`);
      const sessionData = response.data.data || response.data;
      setSession(sessionData);
    } catch (err: any) {
      console.error('Error loading session:', err);
      setError(err.response?.data?.message || 'Error al cargar la sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    try {
      await api.put(`/mentor/sessions/${session.id}`, {
        status: 'completed',
        mentor_feedback: feedback
      });
      setShowCompleteModal(false);
      loadSession();
    } catch (error) {
      console.error('Error completing session:', error);
      alert('Error al completar la sesión');
    }
  };

  const handleCancelSession = async () => {
    if (!session) return;

    try {
      await api.delete(`/mentor/sessions/${session.id}`, {
        data: { reason: cancellationReason }
      });
      setShowCancelModal(false);
      loadSession();
    } catch (error) {
      console.error('Error cancelling session:', error);
      alert('Error al cancelar la sesión');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      scheduled: { variant: 'info', icon: <Clock size={14} />, label: 'Programada' },
      in_progress: { variant: 'warning', icon: <Video size={14} />, label: 'En progreso' },
      completed: { variant: 'success', icon: <CheckCircle size={14} />, label: 'Completada' },
      cancelled: { variant: 'danger', icon: <XCircle size={14} />, label: 'Cancelada' }
    };

    const config = variants[status] || { variant: 'default', label: status };
    return (
      <Badge variant={config.variant}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="session-detail-page session-detail-page--loading">
          <LoadingSpinner size="lg" text="Cargando sesión..." />
        </div>
      </MainLayout>
    );
  }

  if (error || !session) {
    return (
      <MainLayout>
        <div className="session-detail-page">
          <div className="error-page">
            <h2>Error</h2>
            <p>{error || 'Sesión no encontrada'}</p>
            <button className="btn btn--primary" onClick={() => navigate('/mentor/sessions')}>
              Volver a sesiones
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="session-detail-page">
        {/* Header */}
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate('/mentor/sessions')}>
            <ArrowLeft size={20} />
          </button>
          <div className="page-header__info">
            <div className="eyebrow">Mentor · Detalle de Sesión</div>
            <h1 className="heading-1">{session.title}</h1>
            <div className="page-meta">
              {getStatusBadge(session.status)}
              <span>·</span>
              <span>{formatDate(session.scheduled_date)}</span>
              <span>·</span>
              <span>{session.duration_minutes || 60} minutos</span>
            </div>
          </div>
          {session.status === 'scheduled' && (
            <div className="page-header__actions">
              <button
                className="btn btn--outline"
                onClick={() => navigate(`/mentor/sessions/${session.id}/edit`)}
              >
                <Edit2 size={18} />
                Editar
              </button>
              <button
                className="btn btn--danger"
                onClick={() => setShowCancelModal(true)}
              >
                <XCircle size={18} />
                Cancelar sesión
              </button>
              <button
                className="btn btn--success"
                onClick={() => setShowCompleteModal(true)}
              >
                <CheckCircle size={18} />
                Completar sesión
              </button>
            </div>
          )}
        </div>

        <div className="session-detail-grid">
          {/* Main Content */}
          <div className="detail-column">
            <Card>
              <CardHeader>
                <CardTitle>Información de la sesión</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-icon">
                      <FileText size={20} />
                    </div>
                    <div className="detail-content">
                      <div className="detail-label">Tema</div>
                      <div className="detail-value">{session.title}</div>
                    </div>
                  </div>

                  {session.description && (
                    <div className="detail-item">
                      <div className="detail-icon">
                        <FileText size={20} />
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Descripción</div>
                        <div className="detail-value">{session.description}</div>
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-icon">
                      <Calendar size={20} />
                    </div>
                    <div className="detail-content">
                      <div className="detail-label">Fecha y hora</div>
                      <div className="detail-value">
                        {formatDate(session.scheduled_date)} {session.scheduled_time ? `a las ${session.scheduled_time}` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-icon">
                      <Clock size={20} />
                    </div>
                    <div className="detail-content">
                      <div className="detail-label">Duración</div>
                      <div className="detail-value">{session.duration_minutes || 60} minutos</div>
                    </div>
                  </div>

                  {session.meeting_link && (
                    <div className="detail-item">
                      <div className="detail-icon">
                        <Video size={20} />
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Link de reunión</div>
                        <div className="detail-value">
                          <a href={session.meeting_link} target="_blank" rel="noopener noreferrer"
                             className="meeting-link">
                            Unirse a la videollamada
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {session.location && (
                    <div className="detail-item">
                      <div className="detail-icon">
                        <MapPin size={20} />
                      </div>
                      <div className="detail-content">
                        <div className="detail-label">Ubicación</div>
                        <div className="detail-value">{session.location}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Feedback Section */}
            {(session.mentor_feedback || session.mentee_feedback || session.meeting_notes) && (
              <Card>
                <CardHeader>
                  <CardTitle>Retroalimentación y Notas</CardTitle>
                </CardHeader>
                <CardBody>
                  {session.mentor_feedback && (
                    <div className="feedback-item">
                      <div className="feedback-header">
                        <MessageSquare size={16} />
                        <strong>Retroalimentación del Mentor</strong>
                      </div>
                      <p>{session.mentor_feedback}</p>
                    </div>
                  )}

                  {session.mentee_feedback && (
                    <div className="feedback-item">
                      <div className="feedback-header">
                        <MessageSquare size={16} />
                        <strong>Retroalimentación del Mentee</strong>
                      </div>
                      <p>{session.mentee_feedback}</p>
                    </div>
                  )}

                  {session.meeting_notes && (
                    <div className="feedback-item">
                      <div className="feedback-header">
                        <FileText size={16} />
                        <strong>Notas de la sesión</strong>
                      </div>
                      <p>{session.meeting_notes}</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {session.status === 'cancelled' && session.cancellation_reason && (
              <Card>
                <CardHeader>
                  <CardTitle>Información de cancelación</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="cancellation-info">
                    <XCircle size={20} />
                    <div>
                      <strong>Motivo:</strong>
                      <p>{session.cancellation_reason}</p>
                      {session.cancelled_at && (
                        <small>Cancelada el {formatDate(session.cancelled_at)}</small>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="sidebar-column">
            <Card>
              <CardHeader>
                <CardTitle>Mentee</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="mentee-info">
                  <div className="mentee-avatar">
                    <User size={32} />
                  </div>
                  <h3>{session.mentee_first_name} {session.mentee_last_name}</h3>
                  {session.mentee_email && (
                    <a href={`mailto:${session.mentee_email}`} className="mentee-email">
                      {session.mentee_email}
                    </a>
                  )}
                  <button
                    className="btn btn--outline btn--block"
                    onClick={() => navigate(`/mentor/mentees/${session.relationship_id}`)}
                  >
                    Ver perfil completo
                  </button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estado de la sesión</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="status-timeline">
                  <div className={`timeline-item ${session.status ? 'completed' : ''}`}>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <strong>Programada</strong>
                      <small>{formatDate(session.scheduled_date)}</small>
                    </div>
                  </div>

                  <div className={`timeline-item ${session.status === 'completed' ? 'completed' : ''}`}>
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <strong>Completada</strong>
                      {session.completed_at && (
                        <small>{formatDate(session.completed_at)}</small>
                      )}
                    </div>
                  </div>

                  {session.status === 'cancelled' && (
                    <div className="timeline-item cancelled">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <strong>Cancelada</strong>
                        {session.cancelled_at && (
                          <small>{formatDate(session.cancelled_at)}</small>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Complete Session Modal */}
        {showCompleteModal && (
          <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Completar Sesión</h2>
                <button className="modal-close" onClick={() => setShowCompleteModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Retroalimentación (opcional)</label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Comparte tus impresiones sobre la sesión..."
                    rows={4}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn--outline"
                  onClick={() => setShowCompleteModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn--success"
                  onClick={handleCompleteSession}
                >
                  <CheckCircle size={18} />
                  Completar sesión
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Session Modal */}
        {showCancelModal && (
          <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Cancelar Sesión</h2>
                <button className="modal-close" onClick={() => setShowCancelModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Motivo de cancelación (opcional)</label>
                  <textarea
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder="Explica brevemente por qué se cancela la sesión..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn--outline"
                  onClick={() => setShowCancelModal(false)}
                >
                  Volver
                </button>
                <button
                  className="btn btn--danger"
                  onClick={handleCancelSession}
                >
                  <XCircle size={18} />
                  Cancelar sesión
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SessionDetailPage;
